import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { startAnimation } from './helpers/animation';
import './Collapsible.scss';

const animationDuration = 300;

const Collapsible = ({
  children,
  isCollapsed,
  collapsedHeightValue,
  collapsedHeightUnit,
  dataSelectorId,
}) => {
  const isInitialCollapsed = useMemo(() => isCollapsed, []);
  const [isRenderContent, setIsRenderContent] = useState(!isCollapsed);
  const [isFirstEffect, setIsFirstEffect] = useState(true);
  const collapsibleRef = useRef(null);
  const contentRef = useRef(null);

  // It is fine to do this
  // https://reactjs.org/docs/hooks-faq.html#how-do-i-implement-getderivedstatefromprops
  if (!isRenderContent && isRenderContent === isCollapsed) {
    setIsRenderContent(true);
  }

  useEffect(() => {
    if (isFirstEffect) {
      setIsFirstEffect(false);
      // Don't animate if first effect & isCollapsed doesn't change
      if (isInitialCollapsed === isCollapsed) return;
    }

    const { current: collapsibleEl } = collapsibleRef;
    const { current: contentEl } = contentRef;
    if (!collapsibleEl || !contentEl) return;
    const contentHeight = contentEl.clientHeight;
    if (isCollapsed) {
      collapsibleEl.style.height = `${contentHeight}px`;
      startAnimation(() => {
        collapsibleEl.style.height = `${collapsedHeightValue}${collapsedHeightUnit}`;
        collapsibleEl.style.overflow = 'hidden';
        setTimeout(() => {
          setIsRenderContent(false);
        }, animationDuration);
      });
    } else {
      collapsibleEl.style.height = `${contentHeight}px`;
      collapsibleEl.style.overflow = 'hidden';
      setTimeout(() => {
        collapsibleEl.style.height = 'auto';
        collapsibleEl.style.overflow = 'inherit';
      }, animationDuration);
    }
  }, [isCollapsed]);

  return (
    <div
      className="collapsible"
      ref={collapsibleRef}
      style={{
        ...isInitialCollapsed && {
          height: `${collapsedHeightValue}${collapsedHeightUnit}`,
          overflow: 'hidden',
        },
      }}
      data-selector-type="collapsible"
      data-selector-collapsed={isCollapsed}
      data-selector={dataSelectorId}
    >
      {(isRenderContent || collapsedHeightValue > 0) && (
        <div ref={contentRef}>
          {children}
        </div>
      )}
    </div>
  );
};

Collapsible.defaultProps = {
  children: null,
  collapsedHeightValue: 0,
  collapsedHeightUnit: 'px',
  dataSelectorId: null,
};

Collapsible.propTypes = {
  children: PropTypes.node,
  collapsedHeightValue: PropTypes.number,
  collapsedHeightUnit: PropTypes.oneOf(['px', 'em', 'rem']),
  isCollapsed: PropTypes.bool.isRequired,
  dataSelectorId: PropTypes.string,
};

export default Collapsible;
